import React from "react";
import { motion } from "framer-motion";
import { FormattedMessage, useIntl } from "react-intl";
import ReactGA from "react-ga";
import TermsModal from "./TermsModal"; // Thay bằng đường dẫn chính xác nếu khác
import { LoadingSpinner } from "./common/LoadingSpinner";
import { ArrowRight } from "iconsax-react";
import { AdSettings } from "../types/ads";
import settings from "../settings";

interface Props {
  showMainContent: boolean;
  setShowMainContent: (val: boolean) => void;
  showTermsModal: boolean;
  setShowTermsModal: (val: boolean) => void;
  isLogoLoading: boolean;
  logoUrl?: string;
  adSettings: AdSettings | null;
}

const IntroScreen: React.FC<Props> = ({
  showMainContent,
  setShowMainContent,
  showTermsModal,
  setShowTermsModal,
  isLogoLoading,
  logoUrl,
  adSettings,
}) => {
  const intl = useIntl();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{
        opacity: showMainContent ? 0 : 1,
        y: showMainContent ? -40 : 0,
      }}
      transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
      className={`flex items-center justify-center pointer-events-auto p-4
         ${
           adSettings?.layout_num === 1 || !adSettings
             ? "min-h-[calc(100vh-203px)] md:min-h-[calc(100vh-143px)] md:max-h-[calc(100vh-143px)]"
             : "min-h-screen"
         }
        `}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="relative overflow-hidden bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-2xl shadow-md border border-gray-100 text-center max-w-md w-full backdrop-blur-sm"
      >
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-green-400/10 to-blue-400/10 rounded-full blur-xl"></div>

        <div className="relative z-10 p-4">
          {/* Header */}
          <motion.div
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">
              <FormattedMessage id="adRenderer.sponsoredBy" />
            </h2>
            <div className="w-20 h-1 bg-[#0065b2] rounded-full mx-auto"></div>
          </motion.div>

          {/* Logo */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mb-8"
          >
            {isLogoLoading ? (
              <div className="w-32 h-32 mx-auto rounded-xl bg-gray-200 animate-pulse"></div>
            ) : (
              <div className="relative group">
                <div className="absolute inset-0 to-purple-400/20 rounded-xl blur"></div>
                <div>
                  <img
                    src={logoUrl || settings.logoPartner}
                    alt="Logo"
                    className="rounded-xl w-32 h-32 mx-auto object-contain filter drop-shadow-md transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
              </div>
            )}
          </motion.div>

          {/* Button */}
          <motion.button
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            onClick={() => {
              setShowMainContent(true);
              // const pageHeader = document.getElementById("header");
              // if (pageHeader && adSettings?.ad_type !== undefined) {
              //   pageHeader.style.display = "none";
              // }

              ReactGA.event({
                category: "Button",
                action: "Click Continue",
                label: "Intro Screen",
              });
            }}
            className="relative overflow-hidden mt-6 px-4 py-3 rounded-full w-full font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed shadow-lg border border-white/20 group"
            style={{
              background: `linear-gradient(135deg, ${
                adSettings?.button_color || "#0065b2"
              }, ${adSettings?.button_color || "#0065b2"}dd)`,
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: "0 8px 32px rgba(0, 123, 255, 0.3)",
            }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
            <span className="relative z-10 flex items-center justify-center gap-2 whitespace-nowrap">
              <FormattedMessage id="adRenderer.continueToConnect" />
              <motion.span
                animate={{ x: [0, 4, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 1.5,
                  ease: "easeInOut",
                }}
                className="text-lg"
              >
                <ArrowRight size={18} />
              </motion.span>
            </span>
          </motion.button>

          {/* Terms */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="mt-6"
          >
            <p className="text-sm text-gray-600 leading-relaxed">
              {intl.formatMessage({ id: "adRenderer.agree" })}{" "}
              <button
                onClick={() => setShowTermsModal(true)}
                className="relative text-primary font-medium hover:text-blue-700 transition-colors duration-200 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full"
              >
                {intl.formatMessage({ id: "adRenderer.termsOfUse" })}
              </button>
            </p>
          </motion.div>
        </div>

        {/* Glow border */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"></div>
      </motion.div>

      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </motion.div>
  );
};

export default IntroScreen;
